<template>
  <section id="events" ref="root" class="events">
    <SectionHeading index="02" title="Events">
      Upcoming dates, tickets and nights for gente coppada.
    </SectionHeading>

    <div v-if="!loading && events.length === 0" class="events__empty">
      <MarqueeText class="events__empty-marquee" :items="['New dates soon', 'Stay coppado']" variant="outline" :speed="60" />
      <div class="events__empty-cta" data-reveal>
        <p>Announcements drop first on our Instagram.</p>
        <a v-magnetic="0.25" class="pill" href="https://www.instagram.com/coppados/" target="_blank" rel="noopener">
          <span class="roll"><span data-text="Follow @coppados">Follow @coppados</span></span>
          <span class="arrow" aria-hidden="true">↗</span>
        </a>
      </div>
    </div>

    <ul
      v-else-if="events.length"
      class="agenda"
      :class="{ 'has-hover': hoveredId !== null }"
      @pointerleave="hoveredId = null"
    >
      <li
        v-for="event in events"
        :key="event.id"
        class="agenda__row"
        :class="{ 'is-hovered': hoveredId === event.id, 'is-past': event.isPast }"
        data-reveal
        @pointerenter="onEnter($event, event)"
      >
        <div class="agenda__thumb">
          <img :src="event.image_url" :alt="event.place" loading="lazy">
        </div>
        <div class="agenda__date">
          <span class="agenda__day">{{ event.day }}</span>
          <span class="agenda__month">{{ event.month }} <br>{{ event.year }}</span>
        </div>
        <div class="agenda__info">
          <h3 class="agenda__place">{{ event.place }}</h3>
          <p class="agenda__location">
            <span
              v-if="flagCodeFor(event)"
              class="fi agenda__flag"
              :class="'fi-' + flagCodeFor(event)"
              role="img"
              :aria-label="'Bandera: ' + event.location"
            />
            {{ event.location }}
          </p>
        </div>
        <div class="agenda__action">
          <span v-if="event.isPast" class="agenda__past">Past</span>
          <a
            v-else
            v-cursor="event.tickets_enabled ? 'Tickets' : ''"
            class="pill agenda__tickets"
            :class="{ 'is-disabled': !event.tickets_enabled || !event.tickets_link }"
            :href="event.tickets_link || null"
            target="_blank"
            rel="noopener"
          >
            {{ event.tickets_enabled ? 'Tickets' : 'Soon' }}
            <span class="arrow" aria-hidden="true">↗</span>
          </a>
        </div>
      </li>
    </ul>

    <HoverPreview :src="previewSrc" :active="hoveredId !== null" />
  </section>
</template>

<script>
import { apiUrl } from '@/services/apiConfig'
import { normalizeFlagCode } from '@/utils/countryFlags'
import motion from '@/mixins/motion'
import SectionHeading from '@/components/ui/SectionHeading.vue'
import MarqueeText from '@/components/ui/MarqueeText.vue'
import HoverPreview from '@/components/ui/HoverPreview.vue'

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

// La columna es DATE y llega como ISO en UTC: leemos el YYYY-MM-DD tal cual
// para no correr el día según la zona horaria del navegador.
function parseEventDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''))
  if (match) return { year: +match[1], month: +match[2] - 1, day: +match[3] }
  const d = new Date(value)
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() }
}

export default {
  name: 'NextDates',
  components: { SectionHeading, MarqueeText, HoverPreview },
  mixins: [motion],
  data() {
    return {
      events: [],
      loading: true,
      error: null,
      hoveredId: null,
      previewSrc: ''
    }
  },
  mounted() {
    this.revealContent()
    this.fetchEvents()
  },
  methods: {
    async fetchEvents() {
      try {
        this.loading = true
        this.error = null
        const response = await fetch(apiUrl('/api/events'))
        if (!response.ok) throw new Error('Error al cargar los eventos')
        const items = await response.json()
        this.events = this.prepare(items)
      } catch (error) {
        console.error('Error fetching events:', error)
        this.error = 'No se pudieron cargar los eventos'
        this.events = []
      } finally {
        this.loading = false
        this.revealContent()
      }
    },
    // Próximas fechas primero (de la más cercana a la más lejana), después las pasadas.
    prepare(items) {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      const mapped = items.map((event) => {
        const { year, month, day } = parseEventDate(event.date)
        const time = new Date(year, month, day).getTime()
        return {
          ...event,
          time,
          isPast: time < today,
          day: String(day).padStart(2, '0'),
          month: MONTHS[month],
          year
        }
      })
      const upcoming = mapped.filter((e) => !e.isPast).sort((a, b) => a.time - b.time)
      const past = mapped.filter((e) => e.isPast).sort((a, b) => b.time - a.time)
      return [...upcoming, ...past]
    },
    flagCodeFor(event) {
      return normalizeFlagCode(event && event.flag_url)
    },
    onEnter(e, event) {
      if (e.pointerType !== 'mouse') return
      this.hoveredId = event.id
      if (event.image_url && this.previewSrc !== event.image_url) this.previewSrc = event.image_url
    }
  }
}
</script>

<style scoped>
.events {
  position: relative;
  z-index: 1;
  background: var(--c-bg);
  padding: calc(var(--section-space) * 0.6) 0 calc(var(--section-space) * 0.4);
}

/* ---------- Sin fechas ---------- */

.events__empty {
  margin-top: 3rem;
}

.events__empty-marquee {
  font-size: clamp(3rem, 1rem + 8vw, 10rem);
  line-height: 1.1;
  text-transform: uppercase;
}

.events__empty-cta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 2.5rem var(--gutter) 0;
  color: var(--c-muted);
  font-size: var(--fs-lead);
}

.events__empty-cta .pill {
  color: #fff;
}

/* ---------- Agenda ---------- */

.agenda {
  list-style: none;
  margin-top: 1rem;
  padding: 0 var(--gutter);
}

.agenda__row {
  display: grid;
  grid-template-columns: minmax(9rem, 14rem) 1fr auto;
  align-items: center;
  gap: 2rem;
  padding: clamp(1.25rem, 0.8rem + 1.2vw, 2.25rem) 0;
  border-bottom: 1px solid var(--c-line);
  transition: opacity 0.5s var(--ease-out);
}

.agenda__thumb {
  display: none;
}

.agenda__date {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.agenda__day {
  font-size: clamp(2.6rem, 1.6rem + 3vw, 5rem);
  line-height: 0.85;
  letter-spacing: -0.03em;
}

.agenda__month {
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  line-height: 1.5;
  color: var(--c-muted);
}

.agenda__info {
  transition: transform 0.6s var(--ease-out);
}

.agenda__place {
  font-size: var(--fs-h3);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1.05;
}

.agenda__location {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;
  color: var(--c-muted);
  font-size: var(--fs-body);
}

.agenda__flag.fi {
  width: 1.35rem;
  height: 1rem;
  border-radius: 2px;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.agenda__past {
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-muted);
}

.agenda__row.is-past {
  opacity: 0.4;
}

@media (hover: hover) {
  .agenda.has-hover .agenda__row:not(.is-hovered) {
    opacity: 0.25;
  }

  .agenda__row.is-hovered {
    opacity: 1;
  }

  .agenda__row.is-hovered .agenda__info {
    transform: translateX(1.5rem);
  }

  .agenda__row.is-hovered .agenda__day {
    color: var(--c-accent);
  }
}

@media (max-width: 760px) {
  .agenda__row {
    grid-template-columns: 4.5rem 1fr;
    grid-template-areas:
      'thumb date'
      'thumb info'
      'thumb action';
    gap: 0.5rem 1.25rem;
    align-items: start;
  }

  .agenda__thumb {
    display: block;
    grid-area: thumb;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    background: var(--c-surface);
  }

  .agenda__thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .agenda__date {
    grid-area: date;
  }

  .agenda__day {
    font-size: 1.6rem;
  }

  .agenda__month br {
    display: none;
  }

  .agenda__info {
    grid-area: info;
  }

  .agenda__action {
    grid-area: action;
    margin-top: 0.5rem;
  }
}
</style>
